import { useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { TrendingUp, Wallet, Calendar, PiggyBank, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

export default function InvestmentSimulator() {
  const { currentTemplate } = useTheme();
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(5);
  const [rateInput, setRateInput] = useState('5');
  const [months, setMonths] = useState(12);
  const [compound, setCompound] = useState(false);
  const [period, setPeriod] = useState('Mensuel');
  const [showSchedule, setShowSchedule] = useState(false);

  const periodMonths = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };

  const result = useMemo(() => {
    const pm = periodMonths[period] || 1;
    const periodRate = rate / 100;
    const totalPeriods = Math.ceil(months / pm);

    let capital = amount;
    let cumulInterest = 0;
    const schedule = [];
    const chartData = [{ mois: 0, simple: amount, compose: amount }];

    // Simple interest
    let simpleTotal = amount;
    // Compound
    let compCapital = amount;

    for (let i = 1; i <= totalPeriods; i++) {
      const simpleInterest = amount * periodRate;
      simpleTotal += simpleInterest;

      const compInterest = compCapital * periodRate;
      compCapital += compInterest;

      const interest = compound ? (capital * periodRate) : (amount * periodRate);
      cumulInterest += interest;
      if (compound) capital += interest;

      schedule.push({
        period: i,
        month: i * pm,
        interest: Math.round(interest * 100) / 100,
        cumulative: Math.round(cumulInterest * 100) / 100,
        capital: Math.round((amount + cumulInterest) * 100) / 100,
      });

      chartData.push({
        mois: i * pm,
        simple: Math.round(simpleTotal * 100) / 100,
        compose: Math.round(compCapital * 100) / 100,
      });
    }

    const totalInterest = cumulInterest;
    const finalAmount = amount + totalInterest;
    const monthlyEquiv = totalPeriods > 0 ? totalInterest / (totalPeriods * pm) : 0;
    const roi = amount > 0 ? (totalInterest / amount) * 100 : 0;
    const compoundBonus = compCapital - simpleTotal;

    return { totalInterest, finalAmount, monthlyEquiv, roi, schedule, chartData, compoundBonus, simpleTotal, compCapital, totalPeriods };
  }, [amount, rate, months, compound, period]);

  const fmt = (v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="investment-simulator">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Simulateur de gains</h1>
          <p className="text-sm text-muted-foreground mt-1">Estimez vos rendements selon vos paramètres</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Parameters */}
          <div className="space-y-4">
            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-4 sm:p-6 space-y-6">
                {/* Amount */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Capital</Label>
                    <div className="flex items-center gap-1">
                      <Input type="number" value={amount} onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                        className="w-28 text-right text-sm h-8" />
                      <span className="text-sm text-muted-foreground">€</span>
                    </div>
                  </div>
                  <Slider value={[amount]} onValueChange={([v]) => setAmount(v)} min={1000} max={500000} step={1000} />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>1 000 €</span><span>500 000 €</span></div>
                </div>

                {/* Rate */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Taux</Label>
                    <div className="flex items-center gap-1">
                      <Input type="text" inputMode="decimal" value={rateInput} 
                        onChange={(e) => {
                          const val = e.target.value.replace(',', '.');
                          setRateInput(val);
                          const num = parseFloat(val);
                          if (!isNaN(num) && num >= 0) setRate(num);
                        }}
                        className="w-20 text-right text-sm h-8" />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                  <Slider value={[rate]} onValueChange={([v]) => { setRate(v); setRateInput(String(v)); }} min={0.1} max={15} step={0.1} />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>0.1%</span><span>15%</span></div>
                </div>

                {/* Duration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Durée</Label>
                    <Badge variant="outline" className="text-sm">{months} mois</Badge>
                  </div>
                  <Slider value={[months]} onValueChange={([v]) => setMonths(v)} min={1} max={60} step={1} />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>1 mois</span><span>60 mois</span></div>
                </div>

                {/* Period */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Versement</Label>
                  <div className="grid grid-cols-2 gap-1">
                    {['Mensuel', 'Trimestriel', 'Semestriel', 'Annuel'].map(p => (
                      <button key={p} onClick={() => setPeriod(p)}
                        className={`text-xs py-2 rounded border transition-colors ${period === p ? 'border-primary bg-primary/20 text-primary font-semibold' : 'border-border/50 text-muted-foreground hover:border-primary/30'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Compound toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/30">
                  <div>
                    <p className="text-sm font-medium">Intérêts composés</p>
                    <p className="text-[10px] text-muted-foreground">Les intérêts génèrent des intérêts</p>
                  </div>
                  <Switch
                    checked={compound}
                    onCheckedChange={setCompound}
                    data-testid="compound-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Presets */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: '5K / 12m', a: 5000, r: 5, m: 12 },
                { label: '10K / 24m', a: 10000, r: 5, m: 24 },
                { label: '25K / 36m', a: 25000, r: 7, m: 36 },
                { label: '50K / 12m', a: 50000, r: 9, m: 12 },
                { label: '100K / 24m', a: 100000, r: 8, m: 24 },
              ].map(p => (
                <button key={p.label} onClick={() => { setAmount(p.a); setRate(p.r); setRateInput(String(p.r)); setMonths(p.m); }}
                  className="px-3 py-1 rounded-full border border-border/50 text-[10px] text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right - Results */}
          <div className="lg:col-span-2 space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="bg-card/80 border-border/50">
                <CardContent className="p-3 sm:p-4 text-center">
                  <Wallet className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-[10px] text-muted-foreground">Capital</p>
                  <p className="text-sm sm:text-lg font-bold">{fmt(amount)} €</p>
                </CardContent>
              </Card>
              <Card className="bg-card/80 border-border/50">
                <CardContent className="p-3 sm:p-4 text-center">
                  <TrendingUp className="w-4 h-4 mx-auto mb-1" style={{ color: primaryColor }} />
                  <p className="text-[10px] text-muted-foreground">Intérêts totaux</p>
                  <p className="text-sm sm:text-lg font-bold" style={{ color: primaryColor }}>+{fmt(result.totalInterest)} €</p>
                </CardContent>
              </Card>
              <Card className="bg-card/80 border-border/50">
                <CardContent className="p-3 sm:p-4 text-center">
                  <PiggyBank className="w-4 h-4 mx-auto mb-1 text-emerald-500" />
                  <p className="text-[10px] text-muted-foreground">Capital final</p>
                  <p className="text-sm sm:text-lg font-bold text-emerald-500">{fmt(result.finalAmount)} €</p>
                </CardContent>
              </Card>
              <Card className="bg-card/80 border-border/50">
                <CardContent className="p-3 sm:p-4 text-center">
                  <Calendar className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-[10px] text-muted-foreground">ROI</p>
                  <p className="text-sm sm:text-lg font-bold">+{result.roi.toFixed(1)}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Chart - Simple vs Compound comparison */}
            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Évolution du capital</p>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded" style={{ backgroundColor: primaryColor }}></span>Simple</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded bg-emerald-500"></span>Composé</span>
                  </div>
                </div>
                <div className="h-48 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={result.chartData}>
                      <defs>
                        <linearGradient id="gradSimple" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={primaryColor} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradCompose" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="mois" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}m`} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => [`${fmt(Number(value))} €`]}
                        labelFormatter={(label) => `Mois ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: 'none', borderRadius: 8, fontSize: 11 }}
                      />
                      <Area type="monotone" dataKey="simple" stroke={primaryColor} fill="url(#gradSimple)" strokeWidth={2} name="Simple" />
                      <Area type="monotone" dataKey="compose" stroke="#10B981" fill="url(#gradCompose)" strokeWidth={2} strokeDasharray={compound ? "0" : "4 4"} name="Composé" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {result.compoundBonus > 0 && (
                  <p className="text-xs text-center mt-2 text-emerald-400">
                    Bonus intérêts composés : +{fmt(result.compoundBonus)} € par rapport aux intérêts simples
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Monthly breakdown bar chart */}
            <Card className="bg-card/80 border-border/50">
              <CardContent className="p-4 sm:p-6">
                <p className="text-sm font-medium mb-3">Intérêts par période ({period.toLowerCase()})</p>
                <div className="h-32 sm:h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={result.schedule.slice(0, 24)}>
                      <XAxis dataKey="period" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}€`} />
                      <Tooltip
                        formatter={(value) => [`${fmt(Number(value))} €`]}
                        labelFormatter={(label) => `Période ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: 'none', borderRadius: 8, fontSize: 11 }}
                      />
                      <Bar dataKey="interest" fill={primaryColor} radius={[3, 3, 0, 0]} name="Intérêts" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Schedule toggle */}
            <Card className="bg-card/80 border-border/50">
              <button className="w-full p-4 flex items-center justify-between text-left" onClick={() => setShowSchedule(!showSchedule)}>
                <div>
                  <p className="text-sm font-medium">Échéancier détaillé</p>
                  <p className="text-[10px] text-muted-foreground">{result.totalPeriods} versements — {fmt(result.totalInterest)} € d&apos;intérêts</p>
                </div>
                {showSchedule ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {showSchedule && (
                <CardContent className="p-4 pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left py-2 px-2">#</th>
                          <th className="text-left py-2 px-2">Mois</th>
                          <th className="text-right py-2 px-2">Intérêts</th>
                          <th className="text-right py-2 px-2">Cumulé</th>
                          <th className="text-right py-2 px-2">Capital</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.schedule.map((row) => (
                          <tr key={row.period} className="border-b border-border/20 hover:bg-muted/20">
                            <td className="py-1.5 px-2 text-muted-foreground">{row.period}</td>
                            <td className="py-1.5 px-2">{row.month}</td>
                            <td className="py-1.5 px-2 text-right font-mono" style={{ color: primaryColor }}>+{fmt(row.interest)} €</td>
                            <td className="py-1.5 px-2 text-right font-mono">{fmt(row.cumulative)} €</td>
                            <td className="py-1.5 px-2 text-right font-mono text-emerald-400">{fmt(row.capital)} €</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 font-semibold">
                          <td colSpan={2} className="py-2 px-2">Total</td>
                          <td className="py-2 px-2 text-right font-mono" style={{ color: primaryColor }}>+{fmt(result.totalInterest)} €</td>
                          <td></td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-400">{fmt(result.finalAmount)} €</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
